import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { getActiveClientsApi } from '../../../../api/company';
import useAuth from '../../../../hooks/useAuth';
import { Button } from '../../../../components/ui/Button';
import { SearchableSelect } from '../../../../components/ui/SearchableSelect';
import { cn } from '../../../../utils/cn';
import { matchCommerce, toCommerceOption } from '../../../../utils/commerceSearch';
import { formatCommerceOptionLabel } from '../components/commerceOptionLabel';
import ReminderMessagePreview from '../components/ReminderMessagePreview';
import {
  SAMPLE_VARIANT_LABELS,
  previewDataWarnings,
  templatePreviewErrorText,
} from '../utils/reminderTemplates';

// react-select entrega `{ label, value, data }`: los campos del comercio quedan en `data`.
const filterCommerceOption = (option, query) => matchCommerce(option.data, query);

// Mensaje principal de los datos de ejemplo (varias notas) + las variantes de billing.
const MAIN_VARIANT = 'main';

/**
 * Comercios con contrato activo para "Comercio real". `getActiveClientsApi` no lanza: ante
 * un fallo devuelve `{ error }`. Se piden recién la primera vez que se elige esa fuente.
 */
function useCommerceOptions(enabled) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logisticCompanyId = user?.logisticCompany?._id ?? null;
  const [state, setState] = useState({ loaded: false, options: [], failed: false });

  useEffect(() => {
    if (!enabled || state.loaded || !logisticCompanyId) return undefined;
    let active = true;
    getActiveClientsApi(logisticCompanyId).then((data) => {
      if (!active) return;
      setState({
        loaded: true,
        failed: !Array.isArray(data),
        options: Array.isArray(data) ? data.map((c) => toCommerceOption(c, t('Sin nombre'))) : [],
      });
    });
    return () => {
      active = false;
    };
  }, [enabled, state.loaded, logisticCompanyId, t]);

  return state;
}

/**
 * Vista previa en vivo del editor de plantillas: el mensaje que arma billing con el borrador
 * actual, con datos de ejemplo (y sus variantes: una nota, comercio sin nombre, sin enlace)
 * o con las notas por cobrar de hoy de un comercio real.
 *
 * @param {{ channel: 'email'|'whatsapp',
 *   preview: ReturnType<typeof import('../hooks/useTemplatePreview').default>,
 *   source: 'sample'|'company', onSourceChange: (source: 'sample'|'company') => void,
 *   companyId: string, onCompanyChange: (companyId: string) => void }} props
 */
export default function TemplatePreviewPanel({
  channel,
  preview,
  source,
  onSourceChange,
  companyId,
  onCompanyChange,
}) {
  const { t } = useTranslation();
  const baseId = useId();
  const [variant, setVariant] = useState(MAIN_VARIANT);
  const commerces = useCommerceOptions(source === 'company');
  const { data, error, pending, idle, refresh } = preview;

  const variants = source === 'sample' && Array.isArray(data?.variants) ? data.variants : [];
  const shown =
    variant === MAIN_VARIANT ? data : variants.find((v) => v.variant === variant) ?? data;
  const dataWarnings = data ? previewDataWarnings(data.warnings) : [];

  let idleText = null;
  if (idle === 'empty_body') idleText = t('Escribí el texto para ver la vista previa.');
  if (idle === 'missing_company') idleText = t('Elegí un comercio para ver su mensaje con las notas por cobrar de hoy.');

  return (
    <section className="space-y-3 rounded-lg border border-border bg-bg p-4" aria-labelledby={`${baseId}-title`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={`${baseId}-title`} className="m-0 text-sm font-semibold text-fg">
          {t('Vista previa')}
        </h3>
        <div className="flex items-center gap-2">
          {pending && (
            <span className="text-xs text-fg-muted" role="status">
              {t('Actualizando…')}
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<RefreshCw className="h-4 w-4" />}
            disabled={!!idle}
            onClick={refresh}
          >
            {t('Actualizar')}
          </Button>
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="sr-only">{t('Datos de la vista previa')}</legend>
        <div className="flex flex-wrap gap-4">
          {[
            { value: 'sample', label: 'Datos de ejemplo' },
            { value: 'company', label: 'Comercio real' },
          ].map((option) => (
            <label key={option.value} className="flex cursor-pointer items-center gap-2 text-sm text-fg">
              <input
                type="radio"
                name={`${baseId}-source`}
                className="accent-brand-500"
                checked={source === option.value}
                onChange={() => onSourceChange(option.value)}
              />
              {t(option.label)}
            </label>
          ))}
        </div>
        {source === 'company' && (
          <div className="space-y-1">
            <SearchableSelect
              options={commerces.options}
              value={companyId}
              onChange={(value) => onCompanyChange(value || '')}
              placeholder={t('Buscar por nombre, razón social o RUT…')}
              isClearable
              noOptionsText={t('Sin comercios')}
              filterOption={filterCommerceOption}
              formatOptionLabel={formatCommerceOptionLabel}
              aria-label={t('Comercio de la vista previa')}
            />
            {commerces.failed && (
              <p className="text-xs text-status-error">{t('No se pudieron cargar los comercios.')}</p>
            )}
          </div>
        )}
        {source === 'sample' && variants.length > 0 && (
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('Caso de ejemplo')}>
            {[MAIN_VARIANT, ...variants.map((v) => v.variant)].map((value) => (
              <Button
                key={value}
                size="sm"
                variant={variant === value ? 'primary' : 'outline'}
                aria-pressed={variant === value}
                onClick={() => setVariant(value)}
              >
                {value === MAIN_VARIANT ? t('Varias notas') : t(SAMPLE_VARIANT_LABELS[value] ?? value)}
              </Button>
            ))}
          </div>
        )}
      </fieldset>

      {idleText ? (
        <p className="text-sm text-fg-muted">{idleText}</p>
      ) : (
        <>
          {error && (
            <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
              {templatePreviewErrorText(error, t)}
            </p>
          )}
          {/* Tras un error queda la última vista previa buena, atenuada: es de un borrador
              anterior (otra fuente u otro comercio se descartan en el hook). */}
          {shown && (
            <div className={cn('space-y-2 transition-opacity', (pending || error) && 'opacity-60')}>
              <ReminderMessagePreview
                channel={channel}
                subject={shown.subject}
                html={shown.html}
                text={shown.text}
              />
              <p className="m-0 text-xs text-fg-muted">
                {channel === 'whatsapp' && Number.isInteger(shown.max)
                  ? `${t('Largo del mensaje: {{n}} de {{max}} caracteres.', { n: shown.length, max: shown.max })} `
                  : ''}
                {t('Notas en el mensaje: {{listed}} de {{total}}.', {
                  listed: shown.listedNotes ?? 0,
                  total: shown.totalNotes ?? 0,
                })}
              </p>
              {dataWarnings.length > 0 && (
                <ul className="m-0 list-none space-y-1 p-0">
                  {dataWarnings.map((warning) => (
                    <li key={warning.code} className="flex items-start gap-1.5 text-xs text-status-warning">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                      {/* Mensaje de billing: puede traer tokens {{…}}, nunca pasa por t(). */}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              )}
              {!error && (
                <p className="m-0 text-xs text-fg-muted">
                  {source === 'company'
                    ? t('Con las notas por cobrar de hoy: al enviarse puede cambiar si se pagan o vencen otras.')
                    : t('Con datos de ejemplo.')}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
